// src/components/profile/OrganizationSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  PhotoCamera as PhotoCameraIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ImageManagerModal from '../gestionEvento/formsAjustes/ImageManagerModal';

const OrganizationSettings = () => {
  const { user, updateProfile } = useAuth();
  const [organizationData, setOrganizationData] = useState({
    name: '',
    type: 'club', // club, equipo, federacion, asociacion, instituto
    logo: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: ''
    },
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      youtube: '',
      linkedin: ''
    },
    founded: '',
    categories: [], // categorías deportivas
    achievements: [] // logros destacados
  });
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [tempLogo, setTempLogo] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  const [newAchievement, setNewAchievement] = useState('');

  // Cargar datos de la organización al inicializar
  useEffect(() => {
    if (user?.organization) {
      setOrganizationData(prev => ({
        ...prev,
        ...user.organization
      }));
    }
  }, [user]);

  const handleInputChange = (field) => (event) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setOrganizationData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: event.target.value
        }
      }));
    } else {
      setOrganizationData(prev => ({
        ...prev,
        [field]: event.target.value
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUpdating(true);
    setMessage(null);

    try {
      await updateProfile({ organization: organizationData });
      setMessage({ 
        type: 'success', 
        text: 'Información de la organización actualizada correctamente' 
      });

      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Error al actualizar la información' 
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleLogoUpdate = () => {
    if (!tempLogo.trim()) return;
    
    setOrganizationData(prev => ({
      ...prev,
      logo: tempLogo.trim()
    }));
    setLogoDialogOpen(false);
    setTempLogo('');
  };

  const handleSelectLogoFromModal = (imageUrl) => {
    if (!imageUrl) return;
    setOrganizationData(prev => ({ ...prev, logo: imageUrl }));
    setLogoModalOpen(false);
  };

  const addAchievement = () => {
    if (!newAchievement.trim()) return;
    
    setOrganizationData(prev => ({
      ...prev,
      achievements: [...prev.achievements, newAchievement.trim()]
    }));
    setNewAchievement('');
  };

  const removeAchievement = (index) => {
    setOrganizationData(prev => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index)
    }));
  };

  const organizationTypes = [
    { value: 'club', label: 'Club' },
    { value: 'equipo', label: 'Equipo' },
    { value: 'federacion', label: 'Federación' },
    { value: 'asociacion', label: 'Asociación' },
    { value: 'instituto', label: 'Instituto' },
    { value: 'escuela', label: 'Escuela Deportiva' },
    { value: 'otro', label: 'Otro' }
  ];

  const socialMediaConfig = [
    { key: 'facebook', label: 'Facebook', icon: <FacebookIcon />, color: '#1877F2' },
    { key: 'twitter', label: 'Twitter', icon: <TwitterIcon />, color: '#1DA1F2' },
    { key: 'instagram', label: 'Instagram', icon: <InstagramIcon />, color: '#E4405F' },
    { key: 'youtube', label: 'YouTube', icon: <YouTubeIcon />, color: '#FF0000' },
    { key: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon />, color: '#0A66C2' }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Mi Organización
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Gestiona la información de tu organización, club, equipo o institución
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Información básica */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={3}>
                  <BusinessIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Información Básica
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* Logo */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Avatar
                        src={organizationData.logo}
                        sx={{ 
                          width: 100, 
                          height: 100, 
                          margin: '0 auto',
                          bgcolor: 'primary.light'
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                        <Button
                          startIcon={<PhotoCameraIcon />}
                          onClick={() => setLogoModalOpen(true)}
                          size="small"
                          variant="contained"
                        >
                          Cambiar Logo
                        </Button>
                        <Button
                          onClick={() => setLogoDialogOpen(true)}
                          size="small"
                          variant="text"
                        >
                          Usar URL
                        </Button>
                        {organizationData.logo && (
                          <Button
                            onClick={() => setOrganizationData(prev => ({ ...prev, logo: '' }))}
                            size="small"
                            color="error"
                            variant="outlined"
                          >
                            Quitar
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={9}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Nombre de la organización"
                          value={organizationData.name}
                          onChange={handleInputChange('name')}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Tipo de organización</InputLabel>
                          <Select
                            value={organizationData.type}
                            label="Tipo de organización"
                            onChange={handleInputChange('type')}
                          >
                            {organizationTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Descripción"
                          value={organizationData.description}
                          onChange={handleInputChange('description')}
                          helperText="Describe brevemente tu organización"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                <DescriptionIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Año de fundación"
                          type="number"
                          value={organizationData.founded}
                          onChange={handleInputChange('founded')}
                          inputProps={{ min: 1800, max: new Date().getFullYear() }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Información de contacto */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información de Contacto
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email de contacto"
                      type="email"
                      value={organizationData.email}
                      onChange={handleInputChange('email')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={organizationData.phone}
                      onChange={handleInputChange('phone')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Sitio web"
                      value={organizationData.website}
                      onChange={handleInputChange('website')}
                      placeholder="https://www.ejemplo.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WebsiteIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Dirección */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Dirección
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Calle y número"
                      value={organizationData.address.street}
                      onChange={handleInputChange('address.street')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Ciudad"
                      value={organizationData.address.city}
                      onChange={handleInputChange('address.city')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Estado/Provincia"
                      value={organizationData.address.state}
                      onChange={handleInputChange('address.state')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="País"
                      value={organizationData.address.country}
                      onChange={handleInputChange('address.country')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Código postal"
                      value={organizationData.address.postalCode}
                      onChange={handleInputChange('address.postalCode')}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Redes sociales */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Redes Sociales
                </Typography>

                <Grid container spacing={2}>
                  {socialMediaConfig.map((social) => (
                    <Grid item xs={12} sm={6} md={4} key={social.key}>
                      <TextField
                        fullWidth
                        label={social.label}
                        value={organizationData.socialMedia[social.key]}
                        onChange={handleInputChange(`socialMedia.${social.key}`)}
                        placeholder={`@tu${social.key}`}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {React.cloneElement(social.icon, { 
                                style: { color: social.color },
                                fontSize: 'small'
                              })}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Logros */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Logros Destacados
                </Typography>

                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    fullWidth
                    label="Nuevo logro"
                    value={newAchievement}
                    onChange={(e) => setNewAchievement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAchievement()}
                    placeholder="Ej: Campeón Nacional 2024"
                  />
                  <IconButton 
                    onClick={addAchievement} 
                    color="primary"
                    disabled={!newAchievement.trim()}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                <Box display="flex" flexWrap="wrap" gap={1}>
                  {organizationData.achievements.map((achievement, index) => (
                    <Chip
                      key={index}
                      label={achievement}
                      onDelete={() => removeAchievement(index)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>

                {organizationData.achievements.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Agrega los logros más importantes de tu organización
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Botón guardar */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={updating}
                startIcon={<SaveIcon />}
                size="large"
              >
                {updating ? 'Guardando...' : 'Guardar Información'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Dialog para cambiar logo */}
      <Dialog open={logoDialogOpen} onClose={() => setLogoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PhotoCameraIcon sx={{ mr: 1 }} />
            Cambiar Logo de la Organización
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="URL del logo"
            placeholder="https://ejemplo.com/logo.png"
            value={tempLogo}
            onChange={(e) => setTempLogo(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Ingresa la URL del logo de tu organización"
          />
          
          {tempLogo && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vista previa:
              </Typography>
              <Avatar
                src={tempLogo}
                sx={{ width: 100, height: 100, margin: '0 auto' }}
              >
                <BusinessIcon sx={{ fontSize: 40 }} />
              </Avatar>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleLogoUpdate}
            variant="contained"
            disabled={!tempLogo.trim()}
          >
            Actualizar Logo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para gestionar/seleccionar logo desde biblioteca del usuario */}
      <ImageManagerModal
        open={logoModalOpen}
        onClose={() => setLogoModalOpen(false)}
        onSelectImage={handleSelectLogoFromModal}
        currentImageUrl={organizationData.logo}
        type="logo"
        scope="user"
      />
    </Box>
  );
};

export default OrganizationSettings;
